import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { SportsFootball, Refresh, Home } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Generate a random "bad" score for the error display
const generateBadScore = () => {
  const home = Math.floor(Math.random() * 7) * 7; // Touchdowns
  const away = home + 14 + Math.floor(Math.random() * 21); // Always losing
  return { home, away };
};

// Generate random squares for the mini-grid display
const generateErrorSquares = () => {
  const squares: string[] = [];
  const errorSymbols = ['!', '?', 'X', '⚠', '💥', '🏈', '📉', '🚫'];
  for (let i = 0; i < 25; i++) {
    squares.push(errorSymbols[Math.floor(Math.random() * errorSymbols.length)]);
  }
  return squares;
};

export class ErrorBoundary extends Component<Props, State> {
  private badScore = generateBadScore();
  private errorSquares = generateErrorSquares();

  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.badScore = generateBadScore();
    this.errorSquares = generateErrorSquares();
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            textAlign: 'center',
          }}
        >
          {/* Fumble Icon */}
          <Box
            sx={{
              position: 'relative',
              mb: 3,
            }}
          >
            <SportsFootball
              sx={{
                fontSize: 80,
                color: '#8B4513',
                animation: 'fumble 0.5s ease-in-out infinite alternate',
                '@keyframes fumble': {
                  '0%': { transform: 'rotate(-15deg) translateY(0)' },
                  '100%': { transform: 'rotate(15deg) translateY(-10px)' },
                },
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                top: -10,
                right: -20,
                fontSize: '2rem',
                animation: 'pulse 1s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                },
              }}
            >
              💥
            </Typography>
          </Box>

          {/* Error Title */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#d32f2f',
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 2,
            }}
          >
            Fumble!
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 400 }}
          >
            Looks like we dropped the ball on this play. The squares are all mixed up!
          </Typography>

          {/* Error "Scoreboard" */}
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: '#1a1a1a',
              color: '#fff',
              borderRadius: 2,
              minWidth: 280,
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: '#888', letterSpacing: 2 }}
            >
              Error Scoreboard
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 3,
                my: 1,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  YOUR APP
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontFamily: 'monospace', color: '#ff6b6b' }}
                >
                  {this.badScore.home}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ color: '#666' }}>
                -
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#aaa' }}>
                  ERRORS
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontFamily: 'monospace', color: '#4ade80' }}
                >
                  {this.badScore.away}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Q4 · 0:00 · GAME OVER
            </Typography>
          </Paper>

          {/* Mini Broken Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 0.5,
              mb: 3,
              opacity: 0.7,
            }}
          >
            {this.errorSquares.map((symbol, i) => (
              <Box
                key={i}
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: i % 3 === 0 ? '#ffcdd2' : i % 3 === 1 ? '#fff3e0' : '#e3f2fd',
                  borderRadius: 0.5,
                  fontSize: '0.9rem',
                  animation: `shake ${0.3 + (i % 5) * 0.1}s ease-in-out infinite alternate`,
                  animationDelay: `${i * 0.05}s`,
                  '@keyframes shake': {
                    '0%': { transform: 'translateX(-1px)' },
                    '100%': { transform: 'translateX(1px)' },
                  },
                }}
              >
                {symbol}
              </Box>
            ))}
          </Box>

          {/* Error Details (collapsible in production, shown in dev) */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                maxWidth: 500,
                backgroundColor: '#fff5f5',
                border: '1px solid #ffcdd2',
              }}
            >
              <Typography variant="caption" color="error" sx={{ fontFamily: 'monospace' }}>
                {this.state.error.message}
              </Typography>
            </Paper>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Refresh />}
              onClick={this.handleRetry}
              sx={{ textTransform: 'none' }}
            >
              Call a Timeout (Retry)
            </Button>
            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={this.handleGoHome}
              sx={{ textTransform: 'none' }}
            >
              Return to Locker Room
            </Button>
          </Box>

          {/* Fun footer */}
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 4, fontStyle: 'italic' }}
          >
            "Even the best teams have bad plays. Let's try that one again!"
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}
